import {Badge, Button} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconRefresh} from '@tabler/icons-react';
import React, {useState} from 'react';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {syncOutbox} from 'src/offline/syncManager';
import type {OfflineSession, OutboxItem, SyncRunResult} from 'src/offline/types';
import {useConnectivity} from 'src/offline/useConnectivity';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface SyncCardProps {
  activeSession: OfflineSession | undefined;
  outboxItems: OutboxItem[];
}

export const SyncCard: React.FC<SyncCardProps> = ({
  activeSession,
  outboxItems,
}) => {
  const {isOffline} = useConnectivity();
  const {setAlert} = useAlert();
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastResult, setLastResult] = useState<SyncRunResult | null>(null);

  const syncableCount = outboxItems.filter(
    item => item.status === 'pending' || item.status === 'failed'
  ).length;

  const handleSync = async () => {
    if (!activeSession) return;
    setIsSyncing(true);
    try {
      const result = await syncOutbox(activeSession.id);
      setLastResult(result);
      setAlert(
        `Sync complete: ${result.succeeded} succeeded, ${result.failed} failed.`,
        result.failed > 0 ? 'warning' : 'success'
      );
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <SectionCard title="Sync">
      <Stack p="md" gap="sm">
        <Group gap="sm">
          <Button
            size="sm"
            leftSection={<IconRefresh size={16} />}
            onClick={handleSync}
            loading={isSyncing}
            disabled={!activeSession || isOffline || syncableCount === 0}>
            Sync Now
          </Button>
          <Text size="sm" c="dimmed">
            {syncableCount} item{syncableCount === 1 ? '' : 's'} to sync
          </Text>
        </Group>
        {lastResult && (
          <Alert color={lastResult.failed > 0 ? 'yellow' : 'green'} variant="light">
            <Stack gap={4}>
              <Group gap="xs">
                <Badge color="green" variant="light">
                  {lastResult.succeeded} succeeded
                </Badge>
                <Badge color="red" variant="light">
                  {lastResult.failed} failed
                </Badge>
              </Group>
              {lastResult.results.map(result => (
                <Text size="sm" key={result.itemId}>
                  {result.kind}:{' '}
                  {result.status === 'succeeded' ? result.summary : result.error}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}
      </Stack>
    </SectionCard>
  );
};
